import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_SITE_URL;

if (!SITE_URL) throw new Error("REACT_APP_SITE_URL is required");

const DEFAULT_TITLE = "The Jamoon | Wilderness Lodge in Corbett";
const DEFAULT_DESCRIPTION =
  "Discover The Jamoon, a wilderness lodge beside the Ramganga in Corbett, Uttarakhand. Experience wildlife, birding, photography, forest walks and life off the grid.";
const DEFAULT_IMAGE =
  "https://static.wixstatic.com/media/ddf3da_9994f88265e54d628ad4b3141062adc9~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_85,enc_avif,quality_auto/ddf3da_9994f88265e54d628ad4b3141062adc9~mv2.jpg";

const cleanPath = (path = "/") => {
  const pathname = path.split(/[?#]/)[0] || "/";
  return pathname === "/" ? "/" : `/${pathname.replace(/^\/+|\/+$/g, "")}`;
};

const absoluteUrl = (value) => {
  if (!value) return DEFAULT_IMAGE;
  if (/^https:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

const breadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  "@id": `${SITE_URL}${cleanPath(items[items.length - 1].path)}#breadcrumb`,
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${cleanPath(item.path)}`,
  })),
});

const propertySchema = {
  "@type": "LodgingBusiness",
  "@id": `${SITE_URL}/#lodgingbusiness`,
  name: "The Jamoon",
  url: `${SITE_URL}/`,
  description: DEFAULT_DESCRIPTION,
  telephone: "+91 98713 84780",
  email: "corbett@thejamoon.com",
  image: DEFAULT_IMAGE,
  logo: `${SITE_URL}/logo-full.png`,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Village Jamoon, near Lohachaur, Corbett Tiger Reserve",
    addressRegion: "Uttarakhand",
    addressCountry: "IN",
  },
  sameAs: [
    "https://www.instagram.com/jamooncorbett/",
    "https://www.facebook.com/Jamoon-Corbett-114914090639354",
  ],
};

export default function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  imageAlt = "The Jamoon wilderness lodge beside the Ramganga in Corbett",
  type = "website",
  noindex = false,
  breadcrumbs = [],
  schema = [],
  datePublished,
  dateModified,
}) {
  useEffect(() => {
    document.querySelectorAll("[data-static-seo]").forEach((element) => element.remove());
  }, []);

  const pathname = cleanPath(path);
  const canonical = `${SITE_URL}${pathname}`;
  const socialImage = absoluteUrl(image);
  const pageSchema = {
    "@type": type === "article" ? "Article" : "WebPage",
    "@id": `${canonical}#${type === "article" ? "article" : "webpage"}`,
    url: canonical,
    name: title,
    description,
    image: socialImage,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    ...(type === "article"
      ? {
          headline: title.replace(/ \| The Jamoon Journal$/, ""),
          mainEntityOfPage: canonical,
          author: { "@type": "Organization", name: "The Jamoon", url: `${SITE_URL}/` },
          publisher: {
            "@type": "Organization",
            "@id": `${SITE_URL}/#lodgingbusiness`,
            name: "The Jamoon",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-full.png` },
          },
          ...(datePublished ? { datePublished } : {}),
          ...(dateModified ? { dateModified } : {}),
        }
      : {}),
  };
  const graph = [
    ...(pathname === "/"
      ? [
          propertySchema,
          {
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            url: `${SITE_URL}/`,
            name: "The Jamoon",
            publisher: { "@id": `${SITE_URL}/#lodgingbusiness` },
            inLanguage: "en-IN",
          },
        ]
      : []),
    pageSchema,
    ...(breadcrumbs.length ? [breadcrumbSchema(breadcrumbs)] : []),
    ...(Array.isArray(schema) ? schema : [schema]),
  ].filter(Boolean);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />
      {!noindex && <link rel="canonical" href={canonical} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="The Jamoon" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:alt" content={imageAlt} />
      {type === "article" && datePublished && <meta property="article:published_time" content={datePublished} />}
      {type === "article" && dateModified && <meta property="article:modified_time" content={dateModified} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <script id="jamoon-structured-data" type="application/ld+json">
        {JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}
      </script>
    </Helmet>
  );
}
